import { addDaysToIsoDay, todayIso } from '@/shared/format';

/**
 * The tenant's week (`AM-361`): the day it starts on is the one the company chose in Marca y datos
 * ("Primer día de la semana", `tenant_settings.first_day_of_week`), 0 = Sunday to 6 = Saturday as
 * `Date.getDay` and Mantine count. The session says which one it is (`activeTenant.firstDayOfWeek`)
 * and hands it here once, like the zone (`setActiveTimeZone`), so that the calendars of the console
 * and the portal (`TenantDatesProvider`) and every "esta semana" ({@link startOfWeekIso}) read the
 * same day and a calendar that starts on Sunday never sits beside a week that starts on Monday. The
 * server cuts the week of the boards with the same rule (`TenantWeeks.StartOf`).
 */

export type DayOfWeek = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Monday: what provisioning writes, and the week while there is no tenant to take one from. */
export const DEFAULT_FIRST_DAY_OF_WEEK: DayOfWeek = 1;

let activeFirstDayOfWeek: DayOfWeek = DEFAULT_FIRST_DAY_OF_WEEK;

const listeners = new Set<() => void>();

function isDayOfWeek(value: unknown): value is DayOfWeek {
  return typeof value === 'number' && Number.isInteger(value) && value >= 0 && value <= 6;
}

/**
 * Sets the day the week starts on for every calendar and every "esta semana" of the console.
 * Anything missing or outside 0 to 6 is Monday, the same as a session with no tenant. Whoever
 * subscribed ({@link subscribeActiveFirstDayOfWeek}) hears of a change, which is how the calendars
 * already on screen follow a tenant that changed its day.
 */
export function setActiveFirstDayOfWeek(day: number | null | undefined): void {
  const next = isDayOfWeek(day) ? day : DEFAULT_FIRST_DAY_OF_WEEK;
  if (next === activeFirstDayOfWeek) {
    return;
  }

  activeFirstDayOfWeek = next;
  listeners.forEach((listener) => listener());
}

/** The day the week starts on (see {@link setActiveFirstDayOfWeek}). */
export function getActiveFirstDayOfWeek(): DayOfWeek {
  return activeFirstDayOfWeek;
}

/** Calls `listener` whenever the first day of the week changes. Returns the function that stops it. */
export function subscribeActiveFirstDayOfWeek(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * The first day (`yyyy-MM-dd`) of the week `day` is in: the last `firstDayOfWeek` on or before it.
 * Plain calendar arithmetic over the day, done in UTC like {@link addDaysToIsoDay}, so no zone or
 * change of daylight-saving time moves it.
 */
export function startOfWeekIso(
  day: string,
  firstDayOfWeek: DayOfWeek = getActiveFirstDayOfWeek(),
): string {
  const weekday = new Date(`${day}T00:00:00Z`).getUTCDay();
  return addDaysToIsoDay(day, -((weekday - firstDayOfWeek + 7) % 7));
}

/** The first day of the running week in the zone the console shows dates in ("esta semana"). */
export function startOfCurrentWeek(
  now: Date = new Date(),
  firstDayOfWeek: DayOfWeek = getActiveFirstDayOfWeek(),
): string {
  return startOfWeekIso(todayIso(now), firstDayOfWeek);
}
